import { useEffect, useState } from "react"
import {
  Center,
  Heading,
  Spinner,
  useToast,
} from "@chakra-ui/react"
import { NavLayout } from "../components/NavLayout"
import useAxiosPrivate from "../hooks/useAxiosPrivate"
import { useNavigate, useParams } from "react-router-dom"
import { CustomerForm } from "../components/Forms/CustomerForm"
import { addServerErrors } from "../services/add-server-errors"

type FormData = {
    firstName: string;
    lastName: string;
    streetAddress1: string;
    streetAddress2: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
    email: string;
  };

type Errors = {
    [key: string]: { message: string }
}

const initialFormData: FormData = {
    firstName: "",
    lastName: "",
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
    phone: "",
    email: "",
};

const initialErrors: Errors = {}

export const EditCustomer = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const toast = useToast()
  const { customerId } = useParams()
  const [data, setData] = useState(initialFormData)
  const [errors, setErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const endpoint = `/customers/edit/${customerId}`

  useEffect(() => {
    setLoading(true)
    let isMounted = true
    const controller = new AbortController()

    const getData = async () => {
        try {
          const response = await axiosPrivate.get(endpoint, {
            signal: controller.signal
          })
          const options = structuredClone(response.data.customer.options)
          for (const k in options) {
            options[k] = options[k].join(', ')
          }
          isMounted && setData({ ...response.data.customer, ...{ options: {...options }}})
          setLoading(false)
        } catch (err: any) {
          console.error(err)
          toast({
            title: 'Error',
            description: err.message,
            status: 'error',
            isClosable: true,
          })
          navigate(-1)
        }
    }

    const timeout = setTimeout(getData, 500)

    return () => {
        isMounted = false
        isMounted && controller.abort()
        clearTimeout(timeout)
    }
  }, [axiosPrivate, endpoint, navigate, toast])

  const handleSubmit = async (formData: FormData) => {
    setSubmitting(true)
    try {
      const response = await axiosPrivate.post(endpoint, formData)

      if (!response?.data?.customer?.errors) {
        toast({
          title: 'Success',
          description: 'Customer successfully updated!',
          status: 'success',
          isClosable: true,
        })
        navigate(-1)
      } else {
        addServerErrors(response?.data?.customer?.errors, setErrors)
        toast({
          title: 'Error',
          description: 'Please review any errors and resubmit',
          status: 'error',
          isClosable: true,
        })
      }
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e.message,
        status: 'error',
        isClosable: true,
      })
    }
    setSubmitting(false)
  }

  return (
    <NavLayout
      left={
        <Heading as="h1" size="lg">Edit Customer</Heading>
      }
    >
      {!loading
        ? (
          <CustomerForm edit={false} errors={errors} handleSubmit={handleSubmit} initialFormData={data} loading={submitting} />
        ) : (
          <Center>
            <Spinner
              thickness='4px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='xl'
            />
          </Center>
        )
      }
    </NavLayout>
  )
}
